import React, { useState, useEffect } from "react";
import {
  Box, Paper, Typography, Button, Alert, CircularProgress
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../../api";

/**
 * ResultatBoxe (reminder: this file may already exist — only goToStats navigation updated)
 *
 * The important change is goToStats() -> navigates to "/jugeboxe/stats-eleves?..." so StatsElevesPage route matches.
 * Also: prefer displaying result.winnerName (if present) above the belt. Fallback to previous behavior.
 */

export default function ResultatBoxe({ result: propResult, onHome }) {
  const location = useLocation();
  const navigate = useNavigate();
  const raw = propResult || location?.state?.match;
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const [participantsInfo, setParticipantsInfo] = useState({});
  const [className, setClassName] = useState(null);
  const [loadingClass, setLoadingClass] = useState(false);

  const normalizeMatch = (m) => {
    if (!m) return null;
    if (m.participants) {
      return {
        params: m.params || {},
        rounds: m.rounds || [],
        participants: {
          blueId: m.participants.blueId ?? null,
          redId: m.participants.redId ?? null,
          arbitreId: m.participants.arbitreId ?? null,
          nomBlue: m.participants.nomBlue ?? null,
          nomRed: m.participants.nomRed ?? null,
          nomArbitre: m.participants.nomArbitre ?? null
        },
        winner: m.winner ?? null,
        // prefer explicit winnerName if provided by match payload
        winnerName: m.winnerName ?? m.winnerName ?? null,
        createdAt: m.createdAt || null
      };
    }
    return {
      params: m.params || m.settings || {},
      rounds: m.rounds || [],
      participants: {
        blueId: m.blueId ?? null,
        redId: m.redId ?? null,
        arbitreId: m.arbitreId ?? null,
        nomBlue: m.nomBlue ?? m.blueName ?? null,
        nomRed: m.nomRed ?? m.redName ?? null,
        nomArbitre: m.nomArbitre ?? m.arbitreName ?? null
      },
      winner: m.winner ?? null,
      winnerName: m.winnerName ?? null,
      createdAt: m.createdAt || null
    };
  };

  const match = normalizeMatch(raw);

  useEffect(() => {
    let mounted = true;
    if (!match) return () => { mounted = false; };

    async function fetchParticipants() {
      const ids = [match.participants.blueId, match.participants.redId, match.participants.arbitreId].filter(Boolean);
      const map = {};
      try {
        if (ids.length === 0) {
          setParticipantsInfo({});
          return;
        }
        try {
          const q = ids.join(",");
          const res = await api.get(`/eleves?ids=${encodeURIComponent(q)}`);
          if (mounted && Array.isArray(res.data) && res.data.length > 0) {
            res.data.forEach(e => {
              const id = String(e._id ?? e.id ?? e.id_eleve ?? e.uuid ?? "");
              map[id] = { prenom: e.prenom ?? "", nom: e.nom ?? e.name ?? "", fullName: `${e.prenom ? e.prenom + " " : ""}${e.nom ?? e.name ?? id}` };
            });
            setParticipantsInfo(map);
            return;
          }
        } catch (err) {
          // fallback to single requests
        }

        for (const id of ids) {
          try {
            const r = await api.get(`/eleves/${encodeURIComponent(id)}`);
            const e = r.data || {};
            map[id] = { prenom: e.prenom ?? "", nom: e.nom ?? e.name ?? "", fullName: `${e.prenom ? e.prenom + " " : ""}${e.nom ?? e.name ?? id}` };
          } catch (err) {
            map[id] = { prenom: "", nom: "", fullName: id };
          }
        }
        if (mounted) setParticipantsInfo(map);
      } catch (err) {
        if (mounted) setParticipantsInfo({});
      }
    }

    async function fetchClasseName() {
      const p = match.params || {};
      if (p.classeName || p.classe) {
        setClassName(p.classeName || p.classe);
        return;
      }
      const classeId = p.classeId ?? p.classe ?? null;
      if (!classeId) {
        setClassName("— (manuel)");
        return;
      }
      setLoadingClass(true);
      try {
        const res = await api.get(`/classes/${encodeURIComponent(classeId)}`);
        const c = res.data || {};
        const name = c.nom ?? c.name ?? c.label ?? c.title ?? String(classeId);
        setClassName(name);
      } catch (err) {
        setClassName(String(classeId));
      } finally {
        setLoadingClass(false);
      }
    }

    fetchParticipants();
    fetchClasseName();

    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raw]);

  if (!match) {
    return <Box sx={{ p: 4 }}><Alert severity="error">Aucun match à valider. Recommencez la création du match.</Alert></Box>;
  }

  const firstNameFromFull = (full) => {
    if (!full) return null;
    return String(full).split(" ")[0];
  };

  const fallbackDisplayName = (value, fallback) => {
    if (!value || String(value).trim() === "") return fallback;
    const text = String(value).trim();
    if (/^[a-f0-9]{24}$/i.test(text)) return fallback;
    return text;
  };

  const fighterFullName = (which) => {
    const part = match.participants;
    if (which === "blue") {
      const fallback = fallbackDisplayName(part.nomBlue, "Bleu");
      if (part.blueId && participantsInfo[part.blueId]?.fullName) {
        return fallbackDisplayName(participantsInfo[part.blueId].fullName, fallback);
      }
      return fallback;
    }
    if (which === "red") {
      const fallback = fallbackDisplayName(part.nomRed, "Rouge");
      if (part.redId && participantsInfo[part.redId]?.fullName) {
        return fallbackDisplayName(participantsInfo[part.redId].fullName, fallback);
      }
      return fallback;
    }
    return "—";
  };

  const fighterFirst = (which) => {
    const full = fighterFullName(which);
    return firstNameFromFull(full) || full;
  };

  const arbitreFullName = () => {
    const id = match.participants.arbitreId;
    const fallback = fallbackDisplayName(match.participants.nomArbitre, "—");
    if (id && participantsInfo[id]?.fullName) {
      return fallbackDisplayName(participantsInfo[id].fullName, fallback !== "—" ? fallback : (id || "—"));
    }
    return fallback !== "—" ? fallback : (id || "—");
  };
  const computeFighterPoints = (which) => {
    const roundsData = match.rounds || [];
    const scoreKey = which === "red" ? "scoreRed" : "scoreBlue";
    return roundsData.reduce((total, round) => {
      return total + (round?.[scoreKey]?.poings || 0) + (round?.[scoreKey]?.pieds || 0);
    }, 0);
  };

  const durationLabel = () => {
    const p = match.params || {};
    const min = p.durationMin ?? p.duration?.min ?? 0;
    const sec = p.durationSec ?? p.duration?.sec ?? 0;
    return `${min}m ${sec}s`;
  };

  // navigate to stats page with query params (include from=match so StatsElevesPage hides controls)
  const goToStats = () => {
    const classeId = match.params?.classeId ?? match.params?.classe ?? "";
    const activity = match.params?.activity ?? "Boxe Française";
    const q = new URLSearchParams();
    if (classeId) q.set('classeId', classeId);
    if (activity) q.set('activity', activity);
    q.set('from', 'match');
    navigate(`/jugeboxe/stats-eleves?${q.toString()}`);
  };

  // Determine the display winner name: prefer explicit winnerName, then infer from winner label and participants
  const displayWinnerName = () => {
    if (match.winnerName && String(match.winnerName).trim() !== "") return String(match.winnerName);
    const w = (match.winner || "").toString().toLowerCase();
    if (w.includes("red") || w.includes("rouge")) return fighterFullName("red");
    if (w.includes("blue") || w.includes("bleu")) return fighterFullName("blue");
    if (w.includes("egal") || w.includes("nul") || w.includes("draw")) return "Égalité";
    // fallback: try to infer by comparing total points across rounds
    try {
      const roundsData = match.rounds || [];
      let blueTotal = 0, redTotal = 0;
      for (const r of roundsData) {
        blueTotal += (r.scoreBlue?.poings || 0) + (r.scoreBlue?.pieds || 0);
        redTotal += (r.scoreRed?.poings || 0) + (r.scoreRed?.pieds || 0);
      }
      if (blueTotal > redTotal) return fighterFullName("blue");
      if (redTotal > blueTotal) return fighterFullName("red");
    } catch (e) {
      // ignore
    }
    return "—";
  };

  const handleReturn = () => {
    if (typeof onHome === "function") {
      onHome();
      return;
    }
    navigate("/apps/juge-boxe");
  };

  const handleSave = async () => {
    setMsg(null);
    setSaving(true);
    try {
      const rawWinner = match.winner;
      const s = rawWinner ? String(rawWinner).trim().toLowerCase() : "";
      let normalized = null;
      if (s) {
        if (s === 'blue' || s.includes('bleu')) normalized = 'blue';
        else if (s === 'red' || s.includes('rouge')) normalized = 'red';
        else if (s.includes('egal') || s.includes('nul') || s.includes('draw')) normalized = 'draw';
      }

      const payload = {
        classeId: match.params?.classeId ?? match.params?.classe ?? null,
        activity: match.params?.activity ?? match.params?.activityName ?? "Boxe Française",
        roundsCount: match.params?.nbReprises ?? match.params?.rounds ?? (match.rounds ? match.rounds.length : undefined),
        roundDuration: match.params?.roundDuration ?? null,
        params: match.params || {},
        rounds: match.rounds || [],
        blueId: match.participants.blueId || null,
        redId: match.participants.redId || null,
        arbitreId: match.participants.arbitreId || null,
        blueName: match.participants.blueId ? null : (match.participants.nomBlue || null),
        redName: match.participants.redId ? null : (match.participants.nomRed || null),
        winner: normalized,
        createdAt: match.createdAt || new Date().toISOString()
      };

      const res = await api.post("/matchs", payload);
      const data = res.data || {};
      if (data.ok) {
        setMsg({ type: "success", text: "Match enregistré. Statistiques mises à jour." });
      } else {
        setMsg({ type: "success", text: "Match enregistré." });
      }
      setTimeout(() => navigate("/apps/juge-boxe"), 900);
    } catch (err) {
      console.error("Save match failed:", err);
      const message = err?.response?.data?.error || err?.message || "Erreur lors de l'enregistrement.";
      setMsg({ type: "error", text: message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#fafafa", display: "flex", flexDirection: "column", alignItems: "center", py: 3 }}>
      <Paper sx={{ p: { xs: 2, sm: 3 }, width: { xs: "95%", sm: 760 }, borderRadius: 4 }}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, gap: 1, mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>{match.params?.activity ?? "Boxe Française"}</Typography>
          {loadingClass ? <CircularProgress size={20} /> : (
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{className || "—"}</Typography>
          )}
        </Box>

        <Box sx={{ textAlign: "center", mb: 2, bgcolor: "#faf6ff", borderRadius: 4, px: { xs: 2, sm: 3 }, py: { xs: 2.5, sm: 3 } }}>
          <Typography variant="subtitle2" sx={{ color: "#5b3a8e", fontWeight: 800, letterSpacing: 0.4 }}>VAINQUEUR</Typography>
          <Typography variant="h5" sx={{ fontWeight: 900, mb: 1, mt: 0.75 }}>{displayWinnerName()}</Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <img src="/assets/ceintureboxe.png" alt="Ceinture Boxe" style={{ height: 140, objectFit: "contain" }} />
          </Box>
        </Box>

        <Box sx={{ textAlign: "center", mb: 2, px: 2 }}>
          <Typography variant="h6" sx={{ mt: 1 }}>
            {match.params?.nbReprises ?? (match.rounds ? match.rounds.length : "?")} reprise(s) • Durée : {durationLabel()}
          </Typography>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "minmax(180px, 220px) auto minmax(180px, 220px)" }, justifyContent: "center", gap: { xs: 1, sm: 2 }, mt: 2, alignItems: "stretch" }}>
            <Box sx={{ minWidth: 0, bgcolor: "#fff5f5", borderRadius: 3, px: { xs: 1.5, sm: 2 }, py: 1.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{fighterFirst("red")}</Typography>
              <Typography variant="body2" sx={{ color: "#777" }}>{fighterFullName("red")}</Typography>
              <Typography variant="h4" sx={{ mt: 1, fontWeight: 900, color: "#c62828" }}>{computeFighterPoints("red")}</Typography>
              <Typography variant="caption" sx={{ color: "#666" }}>points</Typography>
            </Box>

            <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", justifyContent: "center", px: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>VS</Typography>
            </Box>

            <Box sx={{ minWidth: 0, bgcolor: "#f5faff", borderRadius: 3, px: { xs: 1.5, sm: 2 }, py: 1.5, gridColumn: { xs: "2 / 3", sm: "auto" } }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{fighterFirst("blue")}</Typography>
              <Typography variant="body2" sx={{ color: "#777" }}>{fighterFullName("blue")}</Typography>
              <Typography variant="h4" sx={{ mt: 1, fontWeight: 900, color: "#1565c0" }}>{computeFighterPoints("blue")}</Typography>
              <Typography variant="caption" sx={{ color: "#666" }}>points</Typography>
            </Box>
          </Box>

          <Box sx={{ mt: 2.5, bgcolor: "#f8fbff", borderRadius: 4, px: 2, py: 2 }}>
            <Typography variant="subtitle2" sx={{ color: "#666" }}>Juge Arbitre</Typography>
            <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5 }}>{arbitreFullName()}</Typography>
          </Box>
        </Box>

        {msg && <Alert severity={msg.type} sx={{ mt: 2 }}>{msg.text}</Alert>}

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, gap: 1.5, mt: 3 }}>
          <Button variant="contained" color="success" onClick={handleSave} disabled={saving} sx={{ minHeight: 50, fontWeight: 800 }}>
            {saving ? <><CircularProgress size={16} sx={{ mr: 1 }} /> Sauvegarde...</> : "Sauvegarder le résultat"}
          </Button>
          <Button variant="outlined" onClick={handleReturn} disabled={saving} sx={{ minHeight: 50, fontWeight: 800 }}>Retour</Button>

          <Button
            variant="outlined"
            onClick={goToStats}
            disabled={saving || !(match.params?.classeId ?? match.params?.classe)}
            sx={{ minHeight: 50, fontWeight: 800 }}
          >
            Voir les stats de la classe
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}